import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { businessActions, businessSelectors, businessTypes } from 'app-components/flux/business';
import { mkBusiness } from 'app-components/flux/business/businessTypes';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import BusinessColorSelect from 'components/Business/BusinessColorSelect';
import BusinessLogoField from 'components/Business/BusinessLogoField';

import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import QChoiceTextField from 'components/QuickenControls/QChoiceChips/QChoiceTextField';
import { Map as ImmutableMap } from 'immutable';
import React, { useCallback, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import StdDialogTitle from '../StdDialogTitle';

// @ts-expect-error FIXME TS7006
function reducer(state, action) {
  if (action.payload?.length > 225) {
    return state;
  }
  switch (action.type) {
    case 'industry':
      return { ...state, industry: action.payload };
    case 'businessName':
      return { ...state, businessName: action.payload };
    case 'color':
      return { ...state, color: action.payload };
    case 'structure':
      return {
        ...state,
        structure: action.payload,
        hideBackdrop: action.payload === 'C_CORP',
      };
    case 'taxGroup': {
      return { ...state, taxGroup: action.payload };
    }
    case 'clear-business':
      return { ...state, industry: null, structure: null, businessName: null, color: null };
    case 'line1':
      return { ...state, line1: action.payload };
    case 'line2':
      return { ...state, line2: action.payload };
    case 'state':
      return { ...state, state: action.payload };
    case 'city':
      return { ...state, city: action.payload };
    case 'zipCode':
      return { ...state, zipCode: action.payload };
    case 'email':
      return { ...state, email: action.payload };
    case 'phoneNumber':
      return { ...state, phoneNumber: action.payload };
    case 'logoId':
      return { ...state, logoId: action.payload };
    case 'accountingType':
      return { ...state, accountingType: action.payload };
    default:
      return state;
  }
}

const useStyles = makeStyles()((theme) => ({
  businessField: {
    width: '100%',
  },
  addressField: {
    width: '60%',
  },
  stateField: {
    width: '16%',
    marginLeft: '8px',
    marginRight: '8px',
  },
  zipField: {
    width: '30%',
  },
  nameRow: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
  },
  contactInfo: {
    width: '50%',
    paddingRight: 8,
  },
  contactInfoPhone: {
    width: '50%',
  },
  infoIcon: {
    color: theme.palette.icon.light,
    marginLeft: 8,
    height: 24,
    width: 24,
  },
  row: {
    paddingRight: 32,
    width: '100%',
  },
}));

// @ts-expect-error FIXME TS7006
const init = (initialBusiness) => ({
  hideBackdrop: false,
  industry: initialBusiness?.industry || null,
  structure: initialBusiness?.structure || null,
  businessName: initialBusiness?.name || null,
  color: initialBusiness?.color || null,
  taxGroup: initialBusiness?.taxGroup || null,
  line1: initialBusiness?.address?.line1 || null,
  line2: initialBusiness?.address?.line2 || null,
  city: initialBusiness?.address?.city || null,
  state: initialBusiness?.address?.state || '',
  country: initialBusiness?.address?.country || null,
  zipCode: initialBusiness?.address?.zipCode || null,
  email: initialBusiness?.email || null,
  phoneNumber: initialBusiness?.phoneNumber || null,
  logoId: initialBusiness?.logoId || null,
  accountingType: initialBusiness?.accountingType || 'CASH',
});

const errorObject = {
  duplicate: 'Already tracking a business with this name',
  required: 'Required',
};

const accountingArr = [
  { label: 'Cash basis', value: 'CASH' },
  { label: 'Accrual basis', value: 'ACCRUAL' },
];
const tooltip = (
  <span>
    <b>Cash basis (default)</b>
    <br />
    Records revenue when cash is received and expenses when cash is paid out.
    <br />
    <b>Accrual basis</b>
    <br />
    Records revenue and expenses only when services have been rendered, even if unpaid. Note: You can override this
    later in Reports
  </span>
);

type Props = {
  initialBusiness: any;
  onClose: (e: any) => void;
};

export default function BusinessEditDialog(props: Props) {
  const { initialBusiness, onClose } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [state, dispatchReducer] = useReducer(reducer, initialBusiness, init);

  // @ts-expect-error FIXME TS7006
  const updateLocalState = useCallback((type, payload) => dispatchReducer({ type, payload }), []);
  const businessNames = useSelector(businessSelectors.getBusinessNames);

  let businessNameHelper;
  if (state.businessName in businessNames && state.businessName !== initialBusiness.name) {
    businessNameHelper = errorObject.duplicate;
  } else if (state.businessName === '') {
    businessNameHelper = errorObject.required;
  }

  return (
    <Drawer
      anchor='right'
      open={Boolean(open)}
      onClose={onClose}
      PaperProps={{
        sx: { width: 620 },
      }}
    >
      <StdDialogTitle onClose={onClose} showCloseButton={true} showBackButton={false}>
        Edit business
      </StdDialogTitle>

      <StdDialogContent id='business_edit_dialog'>
        <div className={classes.nameRow}>
          <Typography variant='paragraphRegular'>Business information</Typography>
          <TextField
            id='business_name_field'
            label='Business name'
            className={classes.businessField}
            defaultValue={state.businessName}
            margin='normal'
            variant='filled'
            onChange={(e) => {
              updateLocalState('businessName', e.target.value);
            }}
            helperText={businessNameHelper}
            error={!!businessNameHelper}
            sx={{
              pr: 4,
            }}
          />
          <Box display='flex' alignItems='center'>
            <TextField
              id='industry-select-element'
              label='Industry'
              select
              defaultValue={state.industry}
              margin='normal'
              variant='filled'
              onChange={(e) => {
                updateLocalState('industry', e.target.value);
              }}
              sx={{
                pr: 1,
                width: '100%',
              }}
            >
              {Object.keys(businessTypes.industryObj).map((key) => (
                <MenuItem id={key} key={key} value={key}>
                  {/*
                  // @ts-expect-error FIXME TS7053 */}
                  {businessTypes.industryObj[key]}
                </MenuItem>
              ))}
            </TextField>
            <BusinessColorSelect
              currColor={state.color || ''}
              onChange={(e) => {
                updateLocalState('color', e.target.value);
              }}
            />
          </Box>
          <BusinessLogoField initialLogoId={state.logoId} onSelect={(id) => updateLocalState('logoId', id)} />
          <Divider sx={{ my: 2 }} />
          <Typography variant='paragraphRegular'>Accounting</Typography>
          <Box display='flex' alignItems='center' className={classes.row}>
            <TextField
              id='business-structure-select-element'
              label='Business structure'
              defaultValue={state.structure}
              select
              className={classes.contactInfo}
              margin='normal'
              variant='filled'
              onChange={(e) => {
                updateLocalState('structure', e.target.value);
                if (e.target.value === 'C_CORP') {
                  dispatch(
                    rootUiActions.createDialog(
                      rootUiTypes.mkRootUiData({
                        id: 'unsuported-business',
                        type: CONFIRMATION_DIALOG_TYPE,
                        allowNesting: true,
                        props: ImmutableMap(
                          mkConfirmationDialogProps({
                            title: 'Unsupported business structure',
                            content: (
                              <span>
                                Simplifi does not yet support <b>C Corp</b> businesses. <br /> <br />
                                You may still use this business structure, but you will need to create and associate
                                categories to the appropriate tax lines in order to create tax reports.
                              </span>
                            ),
                            confirmLabel: 'Confirm',
                            hideBackdrop: true,
                            denyLabel: null,
                            confirmAction: () => {
                              updateLocalState('hideBackdrop', false);
                            },
                            denyAction: () => {
                              updateLocalState('hideBackdrop', false);
                            },
                          }),
                        ),
                      }),
                    ),
                  );
                }
              }}
            >
              {Object.keys(businessTypes.businessStructureObj).map((key) => (
                <MenuItem key={key} value={key}>
                  {/*
                   // @ts-expect-error FIXME TS7053 */}
                  {businessTypes.businessStructureObj[key]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id='tax-group-select'
              label='Tax Group'
              select
              margin='normal'
              defaultValue={state.taxGroup}
              className={classes.contactInfoPhone}
              onChange={(e) => {
                updateLocalState('taxGroup', e.target.value);
              }}
            >
              {Object.keys(businessTypes.taxGroupNames).map((key: keyof typeof businessTypes.taxGroupNames) => (
                <MenuItem key={key} value={key}>
                  {businessTypes.taxGroupCategoryNames[key]} (i.e. {businessTypes.taxGroupNames[key]})
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box display='flex' alignItems='center' mt={1}>
            <QChoiceTextField
              text='Accounting method'
              QChoiceChipsProps={{
                items: accountingArr,
                labelFromItem: (item) => item.label,
                keyFromItem: (item) => item.value,
                selectedItems: [state.accountingType === 'CASH' ? accountingArr[0] : accountingArr[1]],
                onSelected: (val) => {
                  updateLocalState('accountingType', val[0].value);
                },
              }}
            />
            <InfoTooltipIcon tooltipHTML={tooltip} size='medium' className={classes.infoIcon} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant='paragraphRegular'>Contact info</Typography>
          <Box className={classes.row} display='flex' alignItems='center'>
            <TextField
              id='business-email-field'
              label='Email'
              className={classes.contactInfo}
              defaultValue={state.email}
              onChange={(e) => {
                updateLocalState('email', e.target.value);
              }}
              margin='normal'
              variant='filled'
            />
            <TextField
              id='business-phone-field'
              label='Phone number (optional)'
              className={classes.contactInfoPhone}
              defaultValue={state.phoneNumber}
              onChange={(e) => {
                updateLocalState('phoneNumber', e.target.value);
              }}
              margin='normal'
              variant='filled'
            />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant='paragraphRegular'>Business address (optional)</Typography>
          <TextField
            id='address_line_1_field'
            label='Street address'
            className={classes.row}
            margin='normal'
            variant='filled'
            defaultValue={state.line1}
            onChange={(e) => {
              updateLocalState('line1', e.target.value);
            }}
          />
          <TextField
            id='address_line_2_field'
            label='Address line 2'
            className={classes.row}
            margin='normal'
            variant='filled'
            defaultValue={state.line2}
            onChange={(e) => {
              updateLocalState('line2', e.target.value);
            }}
          />
          <Box display='flex' width='100%' className={classes.row} alignItems='center'>
            <TextField
              id='city_field'
              label='City'
              className={classes.addressField}
              margin='normal'
              variant='filled'
              defaultValue={state.city}
              onChange={(e) => {
                updateLocalState('city', e.target.value);
              }}
            />
            <TextField
              id='state_field'
              label='State'
              className={classes.stateField}
              margin='normal'
              variant='filled'
              defaultValue={state.state}
              onChange={(e) => {
                updateLocalState('state', e.target.value);
              }}
              select
            >
              {businessTypes.usStatesArray.map((s) => (
                <MenuItem key={s} id={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id='zip_field'
              label='ZIP Code'
              className={classes.zipField}
              margin='normal'
              variant='filled'
              defaultValue={state.zipCode}
              onChange={(e) => {
                updateLocalState('zipCode', e.target.value);
              }}
            />
          </Box>
        </div>
      </StdDialogContent>
      <StdDialogActions
        primary={{
          id: 'save-business-btn',
          variant: 'contained',
          children: 'Save',
          onClick: (e) => {
            dispatch(
              businessActions.updateBusiness(
                mkBusiness({
                  id: initialBusiness.id,
                  industry: state.industry,
                  structure: state.structure,
                  name: state.businessName,
                  color: state.color,
                  taxGroup: state.taxGroup,
                  email: state.email,
                  phoneNumber: state.phoneNumber,
                  logoId: state.logoId,
                  accountingType: state.accountingType,
                  address: {
                    line1: state.line1,
                    line2: state.line2,
                    city: state.city,
                    state: state.state,
                    country: state.country,
                    zipCode: state.zipCode,
                  },
                }),
              ),
            );
            onClose(e);
          },
          disabled: Boolean(businessNameHelper),
        }}
        secondary={{
          id: 'cancel-edit',
          variant: 'text',
          children: 'Cancel',
          onClick: onClose,
        }}
      />
    </Drawer>
  );
}
